'use client'

import { Accordion as BaseAccordion } from '@base-ui/react/accordion'
import { LuMinus, LuPlus } from 'react-icons/lu'

import { cn } from '@/core/utils/common'

import type { ReactNode } from 'react'

type AccordionItem = {
  id: string
  title: ReactNode
  content: ReactNode
}

type AccordionProps = {
  items: AccordionItem[]
  multiple?: boolean
  defaultOpen?: string[]
  className?: string
}

export default function Accordion({
  items,
  multiple = false,
  defaultOpen = [],
  className,
}: AccordionProps) {
  return (
    <BaseAccordion.Root
      multiple={multiple}
      defaultValue={defaultOpen}
      className={cn('divide-y divide-border border-y border-border', className)}
    >
      {items.map((item) => (
        <BaseAccordion.Item key={item.id} value={item.id}>
          <BaseAccordion.Header>
            <BaseAccordion.Trigger
              className={cn(
                'group flex w-full cursor-pointer items-center justify-between gap-4 py-4 over10:py-5',
                'type-ui text-left font-medium transition-colors hover:text-point'
              )}
            >
              {item.title}
              <LuPlus
                aria-hidden
                className="size-5 shrink-0 transition-transform duration-200 group-data-[panel-open]:hidden"
              />
              <LuMinus
                aria-hidden
                className="hidden size-5 shrink-0 transition-transform duration-200 group-data-[panel-open]:block"
              />
            </BaseAccordion.Trigger>
          </BaseAccordion.Header>
          <BaseAccordion.Panel
            className={cn(
              'grid grid-rows-[1fr] opacity-100 transition-[grid-template-rows,opacity]',
              'duration-200 data-[starting-style]:grid-rows-[0fr]',
              'data-[starting-style]:opacity-0 data-[ending-style]:grid-rows-[0fr]',
              'data-[ending-style]:opacity-0'
            )}
          >
            <div className="overflow-hidden">
              <div className="type-ui pb-5 pr-9 leading-7 text-gray">
                {item.content}
              </div>
            </div>
          </BaseAccordion.Panel>
        </BaseAccordion.Item>
      ))}
    </BaseAccordion.Root>
  )
}
